import type { ReactNode } from "react";
import { motion, type Transition } from "framer-motion";

import {
  OnboardingSurfaceContext,
  useOnboardingSurface,
} from "../state/useOnboardingSurface";

/**
 * Motion for the onboarding flow, and the context that makes it work.
 *
 * **Why the layers live here and not in the cards.** Every step renders its own
 * `OnboardingModal`, so the card unmounts and remounts on each step change. An
 * entrance animation on the card would replay on every Continue. So the parts
 * that should animate once — the dimmed overlay, the docked sheet's slide-in,
 * the pill — are persistent layers keyed by *surface* in `OnboardingFlow`'s
 * `AnimatePresence`, and the card only animates its contents, sliding in from
 * the right as the visitor moves on.
 *
 * **Why a context** (`useOnboardingSurface`). A layer that is animating out is
 * still mounted, and its card still reads the flow's atoms — which by then say
 * something else: after a sign-in starts, presentation is already `hidden`, and
 * the sheet on its way out would re-render as the centred modal for the length
 * of its exit. The layer's props are frozen at the moment it started leaving,
 * so the presentation it hands down through the context is the one it was
 * showing.
 *
 * Every transform is dropped for visitors who ask for reduced motion
 * (`MotionConfig reducedMotion="user"` in `OnboardingFlow`); the fades stay,
 * so things still arrive rather than jump.
 */

/** Material's "emphasized decelerate": quick off the mark, soft landing. */
const EASE_OUT = [0.05, 0.7, 0.1, 1] as const;
/** And its accelerate, for things leaving. */
const EASE_IN = [0.3, 0, 0.8, 0.15] as const;

const ENTER: Transition = { duration: 0.38, ease: EASE_OUT };
const EXIT: Transition = { duration: 0.18, ease: EASE_IN };

/**
 * How long the docked sheet waits before it slides in on a page load, so it
 * arrives after the page has settled rather than competing with it. Not
 * applied once the visitor is using the flow — minimising and expanding answer
 * a click, and should answer it straight away.
 */
const PAGE_LOAD_DELAY = 0.8;

interface LayerProps {
  isStepChange: boolean;
  children: ReactNode;
}

/** The centred modal: the dimmed page fades, the card rises into place. */
export const ModalLayer = ({ isStepChange, children }: LayerProps) => (
  <OnboardingSurfaceContext.Provider
    value={{ presentation: "modal", isStepChange }}
  >
    <motion.div
      // The page behind is inert while this is up; the flow carries its own
      // heading and its own live region.
      role="presentation"
      className="fixed inset-0 z-[1030] flex flex-col items-center justify-center bg-surface-modal p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.2 } }}
      exit={{ opacity: 0, transition: { duration: 0.2, delay: 0.05 } }}
    >
      {/* Full height so the card's `max-h-full` still has something definite
          to resolve against, now that it is not a direct child of the overlay. */}
      <motion.div
        className="flex h-full w-full flex-col items-center justify-center"
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1, transition: ENTER }}
        exit={{ opacity: 0, y: 12, scale: 0.98, transition: EXIT }}
      >
        {children}
      </motion.div>
    </motion.div>
  </OnboardingSurfaceContext.Provider>
);

interface DockedLayerProps extends LayerProps {
  /** Arriving with the page rather than in answer to a click. */
  isPageLoad: boolean;
}

/**
 * The docked sheet: rises out of the bottom-right corner, and shrinks back
 * into it when minimised — towards where the pill is about to appear.
 *
 * Owns the fixed positioning. A transform on an ancestor would re-anchor a
 * `position: fixed` card to that ancestor, so the card itself is not fixed.
 */
export const SheetLayer = ({
  isStepChange,
  isPageLoad,
  children,
}: DockedLayerProps) => (
  <OnboardingSurfaceContext.Provider
    value={{ presentation: "docked", isStepChange }}
  >
    <motion.div
      className="fixed bottom-4 right-4 z-[1000] origin-bottom-right sm:bottom-6 sm:right-6"
      initial={{ opacity: 0, y: 32, scale: 0.96 }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { ...ENTER, delay: isPageLoad ? PAGE_LOAD_DELAY : 0 },
      }}
      exit={{ opacity: 0, y: 16, scale: 0.9, transition: EXIT }}
    >
      {children}
    </motion.div>
  </OnboardingSurfaceContext.Provider>
);

/** The collapsed pill: pops up out of the corner the sheet just shrank into. */
export const PillLayer = ({
  isPageLoad,
  children,
}: Omit<DockedLayerProps, "isStepChange">) => (
  <OnboardingSurfaceContext.Provider
    value={{ presentation: "docked", isStepChange: false }}
  >
    <motion.div
      className="fixed bottom-4 right-4 z-[1000] origin-bottom-right sm:bottom-6 sm:right-6"
      initial={{ opacity: 0, y: 12, scale: 0.9 }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          type: "spring",
          stiffness: 420,
          damping: 30,
          delay: isPageLoad ? PAGE_LOAD_DELAY : 0,
        },
      }}
      exit={{ opacity: 0, scale: 0.94, transition: { duration: 0.12 } }}
    >
      {children}
    </motion.div>
  </OnboardingSurfaceContext.Provider>
);

/**
 * A card's scrolling contents, sliding in from the right as the visitor moves
 * on to the next question. Nothing moves on a card that is the first on its
 * surface — the layer is already animating that one in.
 */
export const StepContentMotion = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => {
  const { isStepChange } = useOnboardingSurface();
  return (
    <motion.div
      className={className}
      initial={isStepChange ? { opacity: 0, x: 32 } : false}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.28, ease: EASE_OUT }}
    >
      {children}
    </motion.div>
  );
};
